import * as Type from 'library/ts/utilities/modules/Type';

/**
 * @description Filter sub-array between min - max values. Possible to filter objects by property value. Changed stackoverflow.com solution to filter by property name
 * @see {@link http://stackoverflow.com/questions/11827781/values-between-range-in-javascript-array}
 * @param array {Array}
 * @param min {Number}
 * @param max {Number}
 * @param propertyName {String}
 * @return {Array}
 * @example
 * ```typescript
 * // input array - Numbers
 * var array1 = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 ];
 * console.log( filterRange( array1, 3.2, 5.5 ) ) // prints: Array [ 4, 5 ]
 *
 * // input array - Objects
 * var array2 = [
 *   { bitrate: 1, name: 'test' },
 *   { bitrate: 2, name: 'test' },
 *   { bitrate: 3, name: 'test' },
 *   { bitrate: 4, name: 'test' },
 *   { bitrate: 5, name: 'test' },
 *   { bitrate: 6, name: 'test' },
 *   { bitrate: 7, name: 'test' },
 *   { bitrate: 8, name: 'test' },
 *   { bitrate: 9, name: 'test' },
 *   { bitrate: 10, name: 'test' }
 * ];
 * console.log( filterRange( array1, 3.2, 5.5, 'bitrate' ) ); // prints: Array [ { bitrate: 4, name: 'test' }, { bitrate: 5, name: 'test' } ]
 * ```
 */
export const filterRange = ( array: any[], min: number, max: number, propertyName: string ): any[] => {
	const len = array.length;
	let up = -1;
	let down = len;
	let rrange = [];
	const mid = Math.floor( len / 2 );
	while ( up++ < mid && down-- > mid ) {
		if ( ( array[ up ][ propertyName ] || array[ up ] ) >= max || ( array[ down ][ propertyName ] || array[ down ] ) <= min ) {
			break;
		}
		if ( ( array[ up ][ propertyName ] || array[ up ] ) >= min ) {
			rrange.push( array[ up ] );
		}
		if ( ( array[ down ][ propertyName ] || array[ down ] ) <= max ) {
			rrange.push( array[ down ] );
		}
	}

	rrange = removeDuplicates( rrange, 'bitrate' ); // remove duplicates

	return rrange;
};

/**
 * @description Remove duplicates from array of objects, or from array of simple types
 * @param array {Array}
 * @param propertyName {String}
 * @return {Array}
 * @example
 * ```typescript
 * let uniqueArray = removeDuplicates( unfilteredArray, 'myPropertyName' );
 * // array of simple objects
 * let array = [ 1, 1, 4, 5, 6, 'xxx', 'ccc', 'xxx' ];
 * let uniqueArray = removeDuplicates( array );
 * ```
 */
export const removeDuplicates = ( array: any[], propertyName: string = undefined ): any[] => {
	// process array of simple objects
	if ( propertyName === undefined ) {
		return array.filter( ( item, index ) => array.indexOf( item ) === index );
	}

	const new_arr = [];
	const lookup = {};

	for ( const i in array ) {
		lookup[ array[ i ][ propertyName ] ] = array[ i ];
	}

	for ( const i in lookup ) {
		new_arr.push( lookup[ i ] );
	}

	return new_arr;
};

/**
 * @description Sort any the array against any property you want
 * @see {@link http://www.levihackwith.com/code-snippet-how-to-sort-an-array-of-json-objects-by-property}
 * @see {@link http://opnsrce.github.io/code-snippet-how-to-sort-an-array-of-json-objects-by-property}
 * @param {string} propertyName
 * @param {boolean} ascending
 * @return {(firstElement: any, secondElement: any) => any}
 * @example
 * ```typescript
 * var myArray = [ {name:'qwe', index:5}, {name:'qwe', index:1}, {name:'qwe', index:3} ];
 * console.log( myArray.sort( sortByProperty( 'index' ) ), true ); // prints  [ {name:'qwe', index:1}, {name:'qwe', index:3}, {name:'qwe', index:5} ]
 * ```
 */
export const sortByProperty = ( propertyName: string, ascending: boolean = true ): ( firstElement: any, secondElement: any ) => void | any => {
	return ( a: any, b: any ): number => {
		let sortStatus: number = 0;
		if ( a[ propertyName ] < b[ propertyName ] ) {
			sortStatus = ( ascending === true ? -1 : 1 );
		} else if ( a[ propertyName ] > b[ propertyName ] ) {
			sortStatus = ( ascending === true ? 1 : -1 );
		}
		return sortStatus;
	};
};

/**
 * @description Search array of objects by property name and return found object or null
 * @param keyName {String}
 * @param keyValue {Object}
 * @param myArray {Array}
 * @return {Object}
 */
export const search = ( keyName: string, keyValue: any, myArray: any[] = [] ): any => {
	// sanity check
	if ( Type.isEmpty( keyName ) || Type.isEmpty( keyValue ) || Type.isEmpty( myArray ) ) return null;
	// otherwise, loop over array
	for ( let i = 0; i < myArray.length; i++ ) {
		// if match is found
		if ( myArray[ i ][ keyName ] === keyValue ) {
			return myArray[ i ]; // object with matching key: value is found, return it
		}
	}
	return null; // if nothing found return null
};

/**
 * @description Search array of objects by property name and return index of found item or null
 * @param keyName {String}
 * @param keyValue {Object}
 * @param myArray {Array}
 * @return {Number}
 */
export const indexOf = ( keyName: string, keyValue: any, myArray: any[] ): number => {
	for ( let i = 0; i < myArray.length; i++ ) {
		if ( myArray[ i ][ keyName ] === keyValue ) { // value found
			return i; // return index
		}
	}
	return null; // if nothing found return null
};

/**
 * @description Is Array of objects has the object with certain property value?
 * @param array {Array.<{Object}>}
 * @param key {String}
 * @param valueToCheck {*}
 * @return {Boolean}
 */
export const hasObjWithPropValue = ( array: any[], key: string, valueToCheck: any ) => {
	let found: boolean = false;

	array.some(
		( item ) => {
			if ( item[ key ] === valueToCheck ) {
				found = true;
				return true; // this will break the loop once found
			}
		}
	);

	return found;
};

/**
 * @description Array method, swaps array elements at given indexes
 * @param indexA {Number}
 * @param indexB {Number}
 * @param array {Array}
 * @return {Array}
 */
export const swapItems = ( indexA: number, indexB: number, array: any[] ): any[] => {
	const temp = array[ indexA ];
	array[ indexA ] = array[ indexB ];
	array[ indexB ] = temp;
	return array;
};

/**
 * @description Array utility method. Moves item from once index to another
 * @param array {Array}
 * @param from {Number}
 * @param to {Number}
 * @return {Array}
 */
export const moveItem = ( array: any[], from: number, to: number ): any[] => {
	// remove 'from' item and store it
	const element = array.splice( from, 1 )[ 0 ];
	// insert stored item into position 'to'
	array.splice( to, 0, element );
	return array;
};